import { API } from '../../api';
import { Spin, Steps, notification } from 'antd';
import { handleError } from '../../utils/util';
import ConfirmWithTemplates from './dataSourcesCreateSteps/confirmWithTemplates';
import ConfirmWithoutTemplates from './dataSourcesCreateSteps/confirmWithoutTemplates';
import CreateNewSchema from './dataSourcesCreateSteps/createNewSchema';
import Error from './dataSourcesCreateSteps/error';
import FileUpload from './dataSourcesCreateSteps/fileUpload';
import LinkTemplates from './dataSourcesCreateSteps/linkTemplates';
import PropTypes from 'prop-types';
import React from 'react';

const { Step } = Steps;

/**
 * Component that uses step-by-step workflow to allow the user to create a new Data Source.
 */
class DataSourceCreate extends React.Component {
  static propTypes = {
    addDataSource: PropTypes.func.isRequired,
    client: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
    closeModal: PropTypes.func.isRequired,
  };

  state = {
    current: 0,
    error: '',
    file: null,
    fileList: [],
    linkedTemplates: [],
    loading: false,
    fileSchemaName: '',
  };

  onRemove = file => {
    this.setState(state => {
      const index = state.fileList.indexOf(file);
      const newFileList = [...state.fileList];
      newFileList.splice(index, 1);
      return {
        fileList: newFileList,
      };
    });
  };

  /**
   * This is called whenever the user selects a file to uplaod.
   */
  setFile = file => {
    this.setState({
      file,
    });
  };

  /**
   * Called whenever the user selects a new resource/namespace from
   * the list of checkboxes.
   */
  setLinkedTemplates = linkedTemplates => {
    this.setState({
      linkedTemplates,
    });
  };

  /**
   * Import any templates selected by the user into the file
   */
  importTemplatesIntoFile = async fileData => {
    const { linkedTemplates } = this.state;

    for (let i = 0; i < linkedTemplates.length; i += 1) {
      const { resourceType, namespace } = linkedTemplates[i];
      const { client } = this.props;
      const clientId = client.id;
      const fileName = fileData.name;

      await API.post('/mapping/templates/import', null, {
        params: {
          client_id: clientId,
          resourcetype: resourceType,
          namespace,
          file: fileName,
        },
      });
    }
  };

  /**
   * Sends request to data mapping service for new File row to be created.
   * In the API endpoint, the sample data is also uploaded to the S3 bucket.
   * If linked templates are selected by the user, then we also import
   * those templates into the file.
   * Otherwise we create a new mapping for the resourcetype/namespace given
   * by the user (in the optional arguments to the function)
   *
   * @param {String} resourceType
   * @param {String} namespace
   */
  addFile = async (resourceType, namespace) => {
    this.setState({ loading: true });
    const namespaceVal = namespace === '' ? 'default' : namespace;

    const { file, fileSchemaName, linkedTemplates } = this.state;
    const { addDataSource, client } = this.props;
    const clientId = client.id;

    let filePostResponse;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', fileSchemaName);
    formData.append('client_id', clientId);

    try {
      filePostResponse = await API.post('/file', formData);
    } catch (err) {
      handleError(err);
      return;
    }

    const newDataSource = {
      source: filePostResponse.data.name,
      name: filePostResponse.data.name,
      key: filePostResponse.data.id,
    };

    if (linkedTemplates.length > 0) {
      try {
        await this.importTemplatesIntoFile(filePostResponse.data);
        // set resourcetype/namespace here so that the dropdown will be changed.
        // otherwise, it would show an incorrect resourcetype/namespace/file in the dropdown.
        newDataSource.resourcetype = linkedTemplates[0].resourceType;
        newDataSource.namespace = linkedTemplates[0].namespace;
      } catch (e) {
        // TODO: The following error handling is inconsistent and error handling across the app should be refactored.
        // This custom error is in response to a bug and general error handling is out of scope.
        // In addition to the frontend not properly parsing the Error object for a useful error message,
        // the backend returns errors as a JSON object with an error property while the frontend looks for a message property when creating notifications.
        const displayError =
          e.response?.data?.errorType === 'duplicateTemplatePath'
            ? `There is a duplicate path in a selected template. Ensure that there is a single template file for a given resourcetype/namespace. ${e.response.data.message}`
            : e.toString();
        this.setState({
          error: displayError,
          loading: false,
        });
        return;
      }
    } else {
      try {
        await this.createMapping(
          resourceType,
          namespaceVal,
          fileSchemaName,
          filePostResponse.data.id,
        );
        newDataSource.resourcetype = resourceType;
        newDataSource.namespace = namespaceVal;
      } catch (e) {
        this.setState({
          error: e.toString(),
          loading: false,
        });
        return;
      }
    }

    notification.success({
      description: `Success (${filePostResponse.status})`,
      duration: 2,
    });
    addDataSource(newDataSource);
    this.next();
  };

  /**
   * Create new mapping for a newly created file.
   */
  createMapping = async (resourceType, namespace, fileName) => {
    const { client } = this.props;
    const clientId = client.id;
    const mappingData = {
      mappings: [
        {
          file: fileName,
          path: 'id',
          value: '',
          version: process.env.REACT_APP_FEATURE_MAP_STAGING ? 'draft' : 'latest',
          client_id: clientId,
          resourcetype: resourceType,
          namespace,
        },
        {
          file: fileName,
          path: 'resourceType',
          value: resourceType === 'CoveragePlan' ? 'List' : resourceType,
          version: process.env.REACT_APP_FEATURE_MAP_STAGING ? 'draft' : 'latest',
          client_id: clientId,
          resourcetype: resourceType,
          namespace,
        },
      ],
    };

    try {
      await API.post('/mapping', mappingData);
    } catch (e) {
      this.setState({
        error: e.toString(),
        loading: false,
      });
      throw e;
    }
  };

  /**
   * Move modal to next step.
   *
   * @param {String} param1 Generic parameter that can have a few meanings:
   * - If state.current is 0, this is the file schema name.
   * - Otherwise, param1 indicates if 'Skip' was selected, in which case
   * no action should be taken.
   *
   */
  next = param1 => {
    const { current } = this.state;
    const { closeModal } = this.props;

    const updatedState = {};

    /**
     * Moving from file schema naming => File upload
     */
    if (current === 0) {
      updatedState.fileSchemaName = param1;
    }

    if (current === 3) {
      closeModal();
    }

    updatedState.current = (current + 1) % 4;
    this.setState(updatedState);
  };

  render() {
    const { client } = this.props;
    const { current, error, file, fileSchemaName, linkedTemplates, loading } = this.state;
    const createNewSchemaTitle = (
      <>
        <div>Create New Schema</div>
        {fileSchemaName !== '' && current !== 3 && (
          <div style={{ color: '#A9A9A9', marginTop: 0 }}>name: {fileSchemaName}</div>
        )}
      </>
    );
    let confirmContent;

    if (linkedTemplates.length > 0) {
      confirmContent = (
        <ConfirmWithTemplates
          client={client}
          fileSchemaName={fileSchemaName}
          linkedTemplates={linkedTemplates}
          file={file}
          addFile={this.addFile}
        />
      );
    } else {
      confirmContent = (
        <ConfirmWithoutTemplates
          client={client}
          fileSchemaName={fileSchemaName}
          addFile={this.addFile}
        />
      );
    }

    const steps = [
      {
        title: createNewSchemaTitle,
        content: (
          <CreateNewSchema
            next={this.next}
            client={client}
            onSetResourceInfo={this.onSetResourceInfo}
          />
        ),
      },
      {
        title: 'File Upload',
        content: (
          <FileUpload
            next={this.next}
            client={client}
            fileSchemaName={fileSchemaName}
            setFile={this.setFile}
            onRemove={this.onRemove}
            onChange={this.onChange}
            skippable
            includeMarginTop
            loading={loading}
          />
        ),
      },
      {
        title: 'Link Templates',
        content: <LinkTemplates setLinkedTemplates={this.setLinkedTemplates} next={this.next} />,
      },
      {
        title: 'Confirm',
        content: confirmContent,
      },
    ];
    let modalContent = steps[current].content;
    if (error) modalContent = <Error error={error} />;
    return (
      <>
        <Steps current={current} size="small">
          <Step title={createNewSchemaTitle} />
          <Step title="Upload example file" />
          <Step title="Link template" />
          <Step title="Confirm" />
        </Steps>
        <div>
          <Spin spinning={loading}>{modalContent}</Spin>
        </div>
      </>
    );
  }
}

export default DataSourceCreate;
